/**
 * Modul dispecer al site-ului pietei.
 *
 * Dispecerul pietei noteaza comanda unui client care a sunat, pe ACELASI site si
 * prin aceeasi cotatie/rutare ca orice client. Sesiunea vine din POS (prin Hub,
 * semnata de core) in fragmentul linkului `/dispecer#t=…&call=…`, ca sa nu ajunga
 * in logurile vreunui server, si se tine in `sessionStorage` — doar fila
 * dispecerului, nu tot browserul.
 *
 * Telefonul care suna e „apelul curent": se schimba la fiecare apel, iar sesiunea
 * de dispecer ramane (o tura).
 */

export interface DispatcherSession {
  token: string;
  operator: string;
  marketName: string | null;
  expiresAt: string | null;
  /** Serverul a refuzat sesiunea (expirata / firma scoasa): ramane in mod dispecer, blocat. */
  rejected?: boolean;
}

export interface DispatcherCall {
  phone: string;
  name: string | null;
  /** Ultima comanda notata in acest apel (pentru confirmarea de pe bara). */
  lastPlacedCode?: string | null;
  lastPlacedSeller?: string | null;
}

const SESSION_KEY = "deliveryapp.dispatcher.v1";
const CALL_KEY = "deliveryapp.dispatcher.call.v1";
export const DISPATCHER_CHANGED_EVENT = "deliveryapp:dispatcher-changed";

function read<T>(key: string, marketKey: string): T | null {
  try {
    const raw = window.sessionStorage.getItem(`${key}.${marketKey}`);
    return raw === null ? null : (JSON.parse(raw) as T);
  } catch {
    return null;
  }
}

function write(key: string, marketKey: string, value: unknown): void {
  try {
    if (value === null) window.sessionStorage.removeItem(`${key}.${marketKey}`);
    else window.sessionStorage.setItem(`${key}.${marketKey}`, JSON.stringify(value));
  } catch {
    // Fara sessionStorage modul dispecer nu poate tine sesiunea; site-ul ramane cel public.
  }
  try {
    window.dispatchEvent(new CustomEvent(DISPATCHER_CHANGED_EVENT));
  } catch {
    // Mediu fara evenimente (teste): nimic de anuntat.
  }
}

/**
 * Citire PURA (fara scriere: se apeleaza si la randare). O sesiune expirata NU
 * dispare tacit: fila ramane in modul dispecer, iar tokenul se trimite in
 * continuare ca serverul sa refuze comanda — altfel ar pleca drept comanda de
 * pe site, fara marcaj, cu acordul si urmarirea vizitatorului.
 */
export function readDispatcherSession(marketKey: string): DispatcherSession | null {
  const session = read<DispatcherSession>(SESSION_KEY, marketKey);
  if (session === null || typeof session.token !== "string" || session.token.length === 0) return null;
  return session;
}

/** Sesiunea nu mai e buna (expirata dupa ceas sau refuzata de server). */
export function dispatcherSessionExpired(marketKey: string): boolean {
  const session = readDispatcherSession(marketKey);
  if (session === null) return false;
  return session.rejected === true || (session.expiresAt !== null && Date.parse(session.expiresAt) < Date.now());
}

export function markDispatcherSessionRejected(marketKey: string): void {
  const session = readDispatcherSession(marketKey);
  if (session !== null && session.rejected !== true) write(SESSION_KEY, marketKey, { ...session, rejected: true });
}

export function writeDispatcherSession(marketKey: string, session: DispatcherSession | null): void {
  write(SESSION_KEY, marketKey, session);
  if (session === null) write(CALL_KEY, marketKey, null);
}

export function readDispatcherCall(marketKey: string): DispatcherCall | null {
  const call = read<DispatcherCall>(CALL_KEY, marketKey);
  return call !== null && typeof call.phone === "string" ? call : null;
}

export function writeDispatcherCall(marketKey: string, call: DispatcherCall | null): void {
  write(CALL_KEY, marketKey, call);
}

/** Tokenul de dispecer al pietei, daca fila e in modul dispecer. */
export function dispatcherToken(marketKey: string): string | null {
  return readDispatcherSession(marketKey)?.token ?? null;
}

export function isDispatcherMode(marketKey: string): boolean {
  return dispatcherToken(marketKey) !== null;
}

/** `#t=…&call=…&name=…` — fragmentul cu care POS-ul deschide modul dispecer. */
export function readDispatcherHash(hash: string): { token: string | null; phone: string | null; name: string | null } {
  const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const clean = (value: string | null, max: number) => {
    const trimmed = (value ?? "").trim();
    return trimmed.length > 0 ? trimmed.slice(0, max) : null;
  };
  return { token: clean(params.get("t"), 4_000), phone: clean(params.get("call"), 32), name: clean(params.get("name"), 120) };
}
